import { apiClient } from './client'
import type { ApiResponse } from './client'
import type { AssetQaRunViewDto } from '@/types/assetQa'
import type {
    AgentEffort, AssetQaSummaryDto, AssetVersionSummaryDto, EmailCopyActivationViewDto, GenerationFailureDto, WebQaSummaryDto,
} from '@/types/api-generated'
import type { EmailComplianceClass } from '@/types/touchPoint'

export type { AssetQaRunViewDto, AssetQaFindingDto, AssetQaCheckDto } from '@/types/assetQa'
export type { AgentEffort, AssetQaSummaryDto, AssetVersionSummaryDto, EmailCopyActivationViewDto, WebQaSummaryDto } from '@/types/api-generated'

export interface WebAssetCreateDto {
    Name: string
    Type: string
    Status?: string
    CloudStorageFileId?: string
    CloudStorageFileName?: string
    CloudStorageMimeType?: string
    CloudStorageProvider?: string
    /** Dropbox Chooser direct content URL (ORB-320 / orbitalx-api#199). */
    DirectDownloadUrl?: string
}

export interface WebAssetListItemDto {
    Id: string
    Name: string
    Type: string
    Status: string
    CurrentVersionNumber: number
    CurrentVersionFileName: string | null
    CurrentVersionBlobUrl: string | null
    CurrentVersionBlobUrlExpiresInSeconds: number | null
    UpdatedAt: string | null
    UpdatedBy: string | null
}

// One A/B subject-line challenger variant (ORB-453). On write only Subject is needed (the server
// assigns VariantKey by position); on read VariantKey carries the stored key ("B", ...).
export interface EmailSubjectVariantDto {
  VariantKey?: string
  Subject: string
}

export interface WebAssetViewDto {
    Id: string
    ETag: string
    CampaignId: string
    WorkspaceId: string
    WebId: string
    Name: string
    Type: string
    Status: string
    CurrentVersionNumber: number
    CurrentVersionUploadedAt: string | null
    CurrentVersionUploadedBy: string | null
    CurrentVersionBlobUrl: string | null
    CurrentVersionBlobUrlExpiresInSeconds: number | null
    CurrentVersionFileName: string | null
    CurrentVersionFileSizeBytes: number | null
    TotalVersions: number
    DraftHtml: string | null
    /** Unlayer design JSON for Email Template and Email Copy assets — the editable
     *  source the dashboard loads into the Unlayer canvas. DraftHtml is its compiled
     *  output. Copies own their own design (cloned from the bound template at binding
     *  time); templates own their own design directly. */
    DraftDesign: unknown | null
    DraftConversationSessionId: string | null
    DraftUpdatedAt: string | null
    /** Status of the most recent Webster-driven draft generation: "Idle",
     *  "Generating", "Completed", "Failed". Drives EmailBuilder's UI shell:
     *  Generating → rocket animation, Failed → error + Try Again, Completed →
     *  editor. Null on legacy assets that pre-date this field (treated as
     *  "Idle"). */
    DraftGenerationStatus: string | null
    /** Error message persisted alongside the Failed status. */
    DraftGenerationError: string | null
    /** Why the latest draft generation failed; the dashboard owns its wording (ORB-1040). */
    DraftGenerationFailure?: GenerationFailureDto | null
    ApprovalWorkflowId: string | null
    LandingPageId: string | null
    EmailTypeKey: string | null
    // Email Copy fields (null for other asset types)
    Subject: string | null
    // A/B subject-line challenger variants (ORB-453). Empty for a single-subject copy; Subject is variant A.
    SubjectVariants: EmailSubjectVariantDto[]
    CtaText: string | null
    EmailTemplateAssetId: string | null
    /** Already resolved by the API: an asset nobody has set arrives as the current default, never null. */
    GenerationEffort: AgentEffort
    CreatedAt: string
    CreatedBy: string
    UpdatedAt: string | null
    UpdatedBy: string | null
}

export interface AssetVersionSignOffViewDto {
    SignedOffBy: string
    SignedOffAt: string
    SignOffComment?: string | null
}

export interface AssetVersionAIDataViewDto {
    ExtractedTextBlobPath?: string | null
    VectorIndexId?: string | null
    ProcessingStatus: string
    ProcessedAt?: string | null
    ErrorMessage?: string | null
    Vectorizations?: VectorizationRecordViewDto[] | null
    EnrichedAt?: string | null
}

export interface VectorizationRecordViewDto {
    SourceType: string
    IndexName: string
    VectorizedAt: string
    ChunkCount: number
}

export interface AssetVersionViewDto {
    Id: string
    ETag: string
    AssetId: string
    VersionNumber: number
    CampaignId: string
    WorkspaceId: string
    WebId: string
    UploadedAt: string
    UploadedBy: string
    BlobUrl?: string | null
    BlobUrlExpiresInSeconds?: number | null
    ThankYouBlobUrl?: string | null
    /** SAS URL for an email version's rendered HTML (the compiled HTML promoted alongside the
     *  Unlayer design JSON in BlobUrl). Both Email Template and Email Copy versions populate it
     *  (they share one versioning path); null for other asset types. Lets Review render a past
     *  version. */
    RenderedHtmlBlobUrl?: string | null
    /** Email Copy only: the subject lines and button label saved with this version (ORB-303). Null on a version saved
     *  before then, which Review shows by its stored HTML under the copy's own subject. */
    Subject?: string | null
    SubjectVariants?: EmailSubjectVariantDto[] | null
    CtaText?: string | null
    OriginalFileName?: string | null
    FileSizeBytes: number
    MimeType?: string | null
    VersionComment?: string | null
    StatusAtUpload: string
    UploadStatus?: string | null
    SourceUrl?: string | null
    UploadErrorMessage?: string | null
    UploadSource?: string | null
    SignOff?: AssetVersionSignOffViewDto | null
    AIData?: AssetVersionAIDataViewDto | null
    ParentAssetETag?: string | null
}

/**
 * ORB-419: a promoted Landing Page version's stored HTML with the event-display tokens and the
 * event-runtime sentinel resolved server-side against the Web's current online-event timing, so the
 * sign-off review shows live dates + a working countdown instead of raw {{ORBITALX_EVENT_*}}
 * placeholders. The stored version blob stays tokenized (deploy substitutes from it); resolution is
 * read-time, mirroring the editor's /Draft preview.
 */
export interface LandingPageVersionHtmlDto {
    Html: string | null
    ThankYouHtml: string | null
}

export interface ApprovalCommentViewDto {
    CommentId: string
    ParentCommentId?: string | null
    VersionNumber: number
    AuthorId: string
    AuthorName: string
    AuthorProfilePicUrl?: string | null
    AuthorRole: string
    AuthorCompanyName?: string | null
    CreatedAt: string
    Text: string
    Category?: string | null
    ETag: string
}

export interface AddCommentDto {
    ETag: string
    Text: string
    VersionNumber?: number
    Category?: string
    ParentCommentId?: string
    MentionedUserIds?: string[]
}

export interface SignOffDto {
    ETag: string
    Confirmed: boolean
    SignOffComment?: string
}

export type ChangeAssetStatusDto = {
    ETag: string
    /** "Ready for Sign-Off" | "Feedback Submitted" */
    Status: string
}

export type ReopenAssetDto = {
    ETag: string
    Reason?: string
}

export interface WebAssetUpdateDto {
    Id: string
    /** ETag is optional. The server no longer enforces OCC on WebAsset updates
     *  (last-write-wins matches the dashboard's debounced auto-save flow).
     *  Older callers that still send it continue to work; the server ignores
     *  the value. */
    ETag?: string
    CampaignId: string
    Status?: string
    Name?: string
    Type?: string
    // Email Copy fields. Empty string = clear; undefined = leave persisted value.
    Subject?: string
    // A/B subject-line challengers (ORB-453). undefined = leave; [] clears. Variant keys are server-assigned.
    SubjectVariants?: EmailSubjectVariantDto[]
    CtaText?: string
    EmailTemplateAssetId?: string
    /** Compiled HTML output (Unlayer's `exportHtml` result). Used at preview / send time
     *  by EmailRenderService. Applies to both Email Template and Email Copy assets. */
    DraftHtml?: string
    /** Unlayer design JSON — canonical editable source. The dashboard saves this alongside
     *  DraftHtml on every auto-save so re-opening the editor restores exactly what the
     *  user authored. Applies to both Email Template and Email Copy assets. */
    DraftDesign?: unknown
    /** undefined = leave the persisted level. */
    GenerationEffort?: AgentEffort
}

export interface EmailTemplateOptionDto {
    Id: string
    DisplayName: string
}

export interface EmailPreviewRequestDto {
    /** Compiled HTML from the Unlayer editor's `exportHtml` output. When provided, the
     *  server renders this directly (running only the Web-entity variable substitution
     *  pass) so unsaved-keystroke previews don't require a save round-trip. When omitted,
     *  the server renders from the copy's persisted DraftHtml. */
    HtmlOverride?: string
    /** A saved version to render instead of the draft, subject lines and button label included (ORB-303). Review shows
     *  the version a reviewer signs off; the activation editor the version the activation sends. Not sent with
     *  HtmlOverride. */
    VersionNumber?: number
}

/** Optional sample overrides for the template-only preview (Asset Review pane). When omitted,
 *  the server uses its built-in "your body will appear here" / "Call to Action" placeholders. */
export interface EmailTemplatePreviewRequestDto {
    SampleBodyMarkdown?: string
    SampleCtaText?: string
    /** Render this exact HTML through the template's slot/variable substitution instead of the
     *  asset's current DraftHtml — used to preview a past promoted version's stored HTML. */
    HtmlOverride?: string
}

export interface EmailPreviewResponseDto {
    Subject: string
    HtmlBody: string
    /** Non-null when the render succeeded but with a soft issue worth showing
     *  to the user (e.g. the Web doesn't have a destination URL configured yet
     *  so the CTA button is using a `#` placeholder). */
    Warning?: string | null
}

// ORB-362: body for the Email Copy test-send. All fields optional — with no FromSendingDomainId the
// platform notification sender is used; with one, the test sends from that verified domain. The
// recipient is always the authenticated caller (the server ignores any address in the body).
export interface SendTestEmailRequestDto {
    FromSendingDomainId?: string
    ReplyToEmail?: string
    HtmlOverride?: string
    /** A saved version to send instead of the draft, as an activation would (ORB-303). Not sent with HtmlOverride. */
    VersionNumber?: number
    /** ORB-924: the touchpoint's class, so the test's unsubscribe footer matches the real send. The copy editor omits it. */
    ComplianceClass?: EmailComplianceClass
}

// ORB-362: result of a test send. Sent=true carries the provider message id; false carries a
// human-readable FailureReason for the toast (e.g. domain not verified, provider rejected).
export interface SendTestEmailResultDto {
    Sent: boolean
    ProviderMessageId?: string | null
    FailureReason?: string | null
}

// ORB-243: response shape for the bulk-generate endpoint.
export interface BulkGeneratedEmailAssetDto {
    AssetId: string
    EmailTypeKey: string
    Name: string
    /** Whether Webster is building it. False when there was no template design to clone or the build
     *  could not be queued, so nothing will finish it. */
    Queued: boolean
}

export interface GenerateAllEmailsResponseDto {
    Created: BulkGeneratedEmailAssetDto[]
    SkippedExisting: string[]
}

export interface AssetVersionUploadDto {
    ETag: string
    AssetId: string
    CampaignId: string
    WorkspaceId: string
    FileName?: string
    MimeType?: string
    FileSizeBytes?: number
    VersionComment?: string
    CloudStorageFileId?: string
    CloudStorageFileName?: string
    CloudStorageMimeType?: string
    CloudStorageProvider?: string
}

// ORB-290: shared with the Unlayer image upload callback in UnlayerDesigner.
// Must match the server's IImageUploadService limits exactly — the client check
// is a UX optimization so we don't post a doomed multipart payload; the server
// is still authoritative.
export const MAX_EMAIL_IMAGE_BYTES = 10 * 1024 * 1024
export const ALLOWED_EMAIL_IMAGE_MIME_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
] as const

export interface EmailImageUploadResultDto {
    url: string
    blobPath: string
    contentHash: string
    sizeBytes: number
}

export interface FieldLimitDto {
    FieldKey: string
    MaxLength: number
    IsRequired: boolean
}

export interface FieldLimitsResponseDto {
    AssetType: string
    Fields: FieldLimitDto[]
}

export const assetApi = {
    /**
     * Returns the field-length + requirement catalog for the given asset type
     * (e.g. "ThankYou", "Web", "OnlineEvent", "Download"). Cached server-side
     * for 10 minutes — safe to call on mount per page.
     */
    getFieldLimits: (
        assetType: string,
    ): Promise<ApiResponse<FieldLimitsResponseDto>> =>
        apiClient.get(`/api/Assets/FieldLimits/${encodeURIComponent(assetType)}`),

    getWebAssetsByWeb: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string
    ): Promise<ApiResponse<WebAssetListItemDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`
        )
    },

    createWebAsset: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string,
        data: WebAssetCreateDto,
        file: File
    ): Promise<ApiResponse<unknown>> => {
        const formData = new FormData()
        formData.append('data', JSON.stringify(data))
        formData.append('file', file)
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`,
            formData
        )
    },

    createWebAssetWithoutFile: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string,
        data: WebAssetCreateDto
    ): Promise<ApiResponse<unknown>> => {
        // Email types are created without a file — Webster generates the content.
        // Send as multipart form data (matching the endpoint's parser) with data only.
        const formData = new FormData()
        formData.append('data', JSON.stringify(data))
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`,
            formData
        )
    },

    // ORB-363: generate a single email copy of a chosen catalog type (the new GenerateEmail endpoint).
    // Returns the created asset so the caller can open its Webster builder while the queue drains.
    generateEmailAsset: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string,
        emailTypeKey: string
    ): Promise<ApiResponse<BulkGeneratedEmailAssetDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets/GenerateEmail`,
            { EmailTypeKey: emailTypeKey }
        )
    },

    createWebAssetByCloudStorage: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string,
        data: WebAssetCreateDto
    ): Promise<ApiResponse<unknown>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets`,
            data
        )
    },

    getAssetDetail: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string
    ): Promise<ApiResponse<WebAssetViewDto>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}`
        )
    },

    getAssetVersions: async (
        clientCompanyId: string,
        workspaceId: string,
        assetId: string
    ): Promise<ApiResponse<AssetVersionViewDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions`
        )
    },

    // ORB-303: every saved version on a web, reduced to its number and sign-off, so the Activations tab can say which
    // version each email activation sends and whether that version may send.
    getAssetVersionsByWeb: async (
        clientCompanyId: string,
        workspaceId: string,
        webId: string
    ): Promise<ApiResponse<AssetVersionSummaryDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Webs/${webId}/AssetVersions`
        )
    },

    // ORB-303: the email activations that send this Email Copy, with the emails each has waiting. Read before a
    // sign-off or a reopen, which change what they send.
    getEmailCopyActivations: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string
    ): Promise<ApiResponse<EmailCopyActivationViewDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Activations`
        )
    },

    getAssetComments: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string
    ): Promise<ApiResponse<ApprovalCommentViewDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Comments`
        )
    },

    addComment: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        dto: AddCommentDto
    ): Promise<ApiResponse<ApprovalCommentViewDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Comments`,
            dto
        )
    },

    signOff: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        dto: SignOffDto
    ): Promise<ApiResponse<WebAssetViewDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/SignOff`,
            dto
        )
    },

    /** Move an asset between review states. Separate from updateAsset because that PUT also writes
     *  the name, subject, CTA and email body — a reviewer may set status and nothing else (ORB-666).
     *  Accepts "Ready for Sign-Off" and "Feedback Submitted" only. */
    changeStatus: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        dto: ChangeAssetStatusDto
    ): Promise<ApiResponse<WebAssetViewDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Status`,
            dto
        )
    },

    /** Reopen a signed-off asset. This is what clears the version's sign-off record and enforces
     *  the web's InternalOnlyReopen setting; a raw status write does neither. */
    reopen: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        dto: ReopenAssetDto
    ): Promise<ApiResponse<WebAssetViewDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Reopen`,
            dto
        )
    },

    updateAsset: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        dto: WebAssetUpdateDto
    ): Promise<ApiResponse<WebAssetViewDto>> => {
        return apiClient.put(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}`,
            dto
        )
    },

    uploadVersion: async (
        clientCompanyId: string,
        workspaceId: string,
        assetId: string,
        data: AssetVersionUploadDto,
        file: File
    ): Promise<ApiResponse<AssetVersionViewDto>> => {
        const formData = new FormData()
        formData.append('data', JSON.stringify(data))
        formData.append('file', file)
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions`,
            formData
        )
    },

    uploadVersionByCloudStorage: async (
        clientCompanyId: string,
        workspaceId: string,
        assetId: string,
        data: AssetVersionUploadDto
    ): Promise<ApiResponse<AssetVersionViewDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions`,
            data
        )
    },

    getVersionStatus: async (
        clientCompanyId: string,
        workspaceId: string,
        assetId: string,
        versionId: string,
        signal?: AbortSignal
    ): Promise<ApiResponse<AssetVersionViewDto>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions/${versionId}`,
            { signal }
        )
    },

    deleteAsset: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string
    ): Promise<ApiResponse<unknown>> => {
        return apiClient.delete(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}`
        )
    },

    promoteDraft: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        options?: { versionComment?: string }
    ): Promise<ApiResponse<unknown>> => {
        const body: Record<string, unknown> = {}
        if (options?.versionComment) body.VersionComment = options.versionComment
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/PromoteDraft`,
            body
        )
    },

    loadVersionIntoDraft: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        versionNumber: number
    ): Promise<ApiResponse<unknown>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/LoadVersionIntoDraft`,
            { VersionNumber: versionNumber }
        )
    },

    // ORB-243: kicks off bulk email-asset generation. Server creates one Draft
    // WebAsset per missing catalog entry (per WebType), then enqueues session-
    // throttled generation messages. 202 Accepted; per-asset progress arrives
    // via the existing emailasset-{assetId} SignalR group.
    generateAllEmails: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string
    ): Promise<ApiResponse<GenerateAllEmailsResponseDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets/GenerateAllEmails`
        )
    },

    // ORB-249: list Email Template-type assets in a Web for the "bind copy to template" dropdown.
    getEmailTemplatesByWeb: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string
    ): Promise<ApiResponse<EmailTemplateOptionDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/EmailTemplates`
        )
    },

    // ORB-289: render an Email Copy into deliverable HTML for the live preview pane.
    // Copies now own their full Unlayer design — the optional HtmlOverride lets the editor
    // preview unsaved-keystroke edits by sending Unlayer's just-compiled HTML without
    // round-tripping a save. Server runs the Web-entity variable pass; no slot merge.
    previewEmailCopy: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        overrides?: EmailPreviewRequestDto,
        signal?: AbortSignal
    ): Promise<ApiResponse<EmailPreviewResponseDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/EmailCopyPreview`,
            overrides ?? {},
            { signal }
        )
    },

    // ORB-362: render this Email Copy and send a one-off TEST to the logged-in user's own address,
    // through the live render + Resend path (no suppression, no delivery guard, no tracking row). The
    // recipient is always the authenticated caller — the server ignores any address in the body.
    sendTestEmail: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        body?: SendTestEmailRequestDto,
    ): Promise<ApiResponse<SendTestEmailResultDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/SendTestEmail`,
            body ?? {},
        )
    },

    // ORB-289: re-clone the currently bound template's DraftDesign onto the copy, discarding
    // the copy's customizations. Server clears DraftHtml so the editor regenerates it on the
    // first post-reset open. Returns the updated copy DTO.
    resetCopyToTemplate: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
    ): Promise<ApiResponse<WebAssetViewDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/ResetCopyToTemplate`,
            {},
        )
    },

    // ORB-290: upload an image from the Unlayer editor's native image picker into our
    // public-images blob container (demandwebs storage account). Server returns a stable
    // CDN URL the editor inserts directly into the design. Content-hash naming means
    // re-uploading the same bytes resolves to the same URL within a workspace.
    uploadEmailImage: async (
        clientCompanyId: string,
        workspaceId: string,
        file: File,
    ): Promise<ApiResponse<EmailImageUploadResultDto>> => {
        const formData = new FormData()
        formData.append('file', file)
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Email/Images`,
            formData,
        )
    },

    // Render an Email Template asset alone (with placeholder body / CTA) for the Asset Review
    // preview pane. Mirrors previewEmailCopy's response shape so the dashboard can reuse the
    // iframe + warning-banner pattern. Server fills in sensible placeholders unless overrides
    // are sent.
    previewEmailTemplate: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        templateAssetId: string,
        overrides?: EmailTemplatePreviewRequestDto,
        signal?: AbortSignal
    ): Promise<ApiResponse<EmailPreviewResponseDto>> => {
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${templateAssetId}/EmailTemplatePreview`,
            overrides ?? {},
            { signal }
        )
    },

    // ORB-419: fetch a promoted LP version's stored HTML with the event-display tokens + runtime
    // sentinel resolved server-side against the Web's current online-event timing. Replaces a direct
    // SAS-blob fetch in Asset Review so the sign-off preview shows live dates + a working countdown
    // instead of raw {{ORBITALX_EVENT_*}} placeholders. Lenient server-side: a non-event page (or one
    // with no scheduled times) comes back unchanged.
    getLandingPageVersionHtml: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        assetId: string,
        versionNumber: number,
    ): Promise<ApiResponse<LandingPageVersionHtmlDto>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Assets/${assetId}/Versions/${versionNumber}/LandingPageHtml`
        )
    },

    // ORB-839. The QA result for a version, or 204 when none was recorded — QA runs asynchronously
    // after an upload, and any version uploaded before ORB-838 shipped will never have one. A 204
    // deserialises to undefined data, which the panel reads as "not checked" rather than an error.
    //
    // Keyed by version id, not version number, unlike the landing-page read above: this route is
    // the one the Model Context Protocol host calls, and it addresses the version directly.
    getVersionQaRun: async (
        clientCompanyId: string,
        workspaceId: string,
        assetId: string,
        versionId: string,
    ): Promise<ApiResponse<AssetQaRunViewDto | undefined>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions/${versionId}/QaRun`
        )
    },

    // ORB-839. Dismisses one finding, with a mandatory reason, and returns the whole run so the
    // panel re-renders from what was stored rather than from what it hoped was stored. Addressed by
    // CheckId rather than code — one code covers a class of mistake, so a run can hold several.
    overrideQaFinding: async (
        clientCompanyId: string,
        workspaceId: string,
        assetId: string,
        versionId: string,
        checkId: string,
        code: string,
        reason: string,
        // ORB-967. Set only when the panel folded more than one finding into the row: the server
        // then dismisses every open member under the key, recording this reason against each.
        groupKey?: string,
        // The run the panel rendered. A version can be re-checked while the reason is being
        // written, and the server acts on the latest run — so this says which run was agreed to,
        // and a mismatch comes back 409 rather than landing on one nobody saw.
        runId?: string,
    ): Promise<ApiResponse<AssetQaRunViewDto>> => {
        // CheckId travels in the body, not the path: a check id can carry a colon and spaces
        // ("speaker-name:James Ford"), which is awkward to round-trip through a route segment.
        return apiClient.post(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Assets/${assetId}/Versions/${encodeURIComponent(versionId)}/QaRun/Findings/Override`,
            // Both: CheckId identifies a finding on any current run, and Code is what a run
            // recorded before that field existed can still be addressed by (ORB-884).
            { CheckId: checkId, Code: code, Reason: reason, GroupKey: groupKey ?? null, RunId: runId ?? null }
        )
    },

    // ORB-889. What QA still owes on each asset of a web, in one read.
    //
    // ⚑ Only assets with something outstanding come back, so an asset absent from the answer is
    // clean — or has never been checked, which this cannot tell apart and does not try to. Both draw
    // nothing, which is the honest rendering of "no open work to report here".
    getWebAssetQaSummary: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        webId: string,
        signal?: AbortSignal,
    ): Promise<ApiResponse<AssetQaSummaryDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/${webId}/Assets/QaSummary`,
            { signal }
        )
    },

    // ORB-889. The same question across a campaign, grouped by web, for its web list.
    getCampaignWebQaSummary: async (
        clientCompanyId: string,
        workspaceId: string,
        campaignId: string,
        signal?: AbortSignal,
    ): Promise<ApiResponse<WebQaSummaryDto[]>> => {
        return apiClient.get(
            `/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/Campaigns/${campaignId}/Webs/QaSummary`,
            { signal }
        )
    },
}
